import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/provider.slice'; // Import your action creators
import * as api from '@/services/provider'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { IPagination } from '../types';
import { IFetchProviders } from '@/services/provider/provider.type';
import { isSameRequestParams } from '@/utils/requestParamsChecker';

// Define action types
export interface GetProviderAction extends PayloadAction<string> {
  type: 'provider/getProvider';
}

export interface GetProvidersAction extends PayloadAction<IFetchProviders> {
  type: 'provider/getProviders';
}

export interface CreateProviderAction extends PayloadAction<Provider> {
  type: 'provider/createProvider';
}

export interface UpdateProviderAction extends PayloadAction<Provider> {
  type: 'provider/updateProvider';
}

export interface DeleteProviderAction extends PayloadAction<DeleteProvider> {
  type: 'provider/deleteProvider';
}

export interface ActivateProviderAction extends PayloadAction<Provider> {
  type: 'provider/activateProvider';
}

// Define the shape of the API response for a single provider
export interface SingleProviderResponse {
  providerId: string;
  partnerId: string;
  name: string;
  createdOn: string;
  updatedOn: string;
  // ... other properties
}
export interface Provider {
  providerId: string;
  partnerId: string;
  name: string;
  createdOn?: string;
  updatedOn?: string;
  version?: string
  server?: string;
  profile?: string;
  configs: string;
  vdiVersion?: string;
  syncVdi?: boolean;
}
export interface ApiResponse {
  success: boolean;
  message: string;
  data: Provider[];
  pagination: IPagination;
}
export interface CreateProvider {
  partnerId?: string;
  name: string;
}

export interface SelectedProvider {
  id?: string;
  name: string;
}

export interface DeleteProvider {
  providerId: string;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data: Provider[];
  pagination: IPagination;
}
export interface ProviderApiPayload {
  success: boolean;
  message: string;
  data: Provider; // The actual created provider object
  syncVdi: boolean;
}

// 4. Interface for the entire Axios/HTTP Response object
// (Use this as the return type for your createProvider API function)
export interface ProviderCreationResponse {
  data: ProviderApiPayload;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: Record<string, any>; // Configuration details
  request: any; // Request details
}

function* getProviders(action: PayloadAction<IFetchProviders>): Generator {
  try {
    const response: any = yield call(api.fetchProviders, action?.payload);
    if (isValidProvidersResponse(response?.data)) {
      yield put(actions.getProvidersSuccess(response?.data));
    } else {
      yield put(actions.getProvidersFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getProvidersFailure(error?.response?.data));
  }
}

// Your saga function
function* getProvider(action: GetProviderAction): Generator {
  try {
    const response: any = yield call(api.fetchProvider, action?.payload);
    const res = response?.data?.data

    if (isValidProviderResponse(res)) {
      yield put({
        type: 'provider/getProviderSuccess',
        payload: res as SingleProviderResponse,
      });
    } else {
      // Forward the API response body (if present) so global error handler
      // can display the actual error message instead of a generic fallback.
      yield put({ type: 'provider/getProviderFailure', payload: response?.data || {} });
    }
  } catch (error: any) {
    // When the network/request fails, propagate the server error body
    yield put({ type: 'provider/getProviderFailure', payload: error?.response?.data || { message: error?.message } });
  }
}

function* createProvider(action: CreateProviderAction): Generator {
  try {
    const response: ProviderCreationResponse = yield call(api.createProvider, action?.payload);
    const responseData = response?.data;
    if (responseData?.success) {
      yield put(actions.createProviderSuccess(responseData));
    } else {
      yield put(actions.createProviderFailure(responseData as unknown as ApiResponse));
    }
  } catch (error: any) {
    yield put(actions.createProviderFailure(error?.response?.data));
  }
}

function* updateProvider(action: UpdateProviderAction): Generator {
  try {
    const response: any = yield call(
      api.updateProvider,
      action?.payload?.providerId,
      action?.payload,
    );
    const responseData = response?.data;
    if (responseData?.success) {
      yield put(actions.updateProviderSuccess(responseData));
    } else {
      yield put(actions.updateProviderFailure(responseData));
    }
  } catch (error: any) {
    yield put(actions.updateProviderFailure(error?.response?.data || error));
  }
}

function* deleteProvider(action: DeleteProviderAction): Generator {

  try {
    const response: any = yield call(api.deleteProvider, action?.payload);
    const responseData = response?.data;
    if (responseData?.success) {
      yield put({
        type: 'provider/deleteProviderSuccess',
        payload: response as SingleProviderResponse,
      });
    } else {
      yield put({ type: 'provider/deleteProviderFailure' });
    }
  } catch (error) {
    yield put({ type: 'provider/deleteProviderFailure', payload: "Something went wrong" });
  }
}

function* activateProvider(action: ActivateProviderAction): Generator {
  try {
    const response: any = yield call(api.activateProvider, action?.payload);
    const responseData = response?.data;
    if (responseData?.success) {
      yield put(actions.activateProviderSuccess(responseData));
    } else {
      yield put(actions.activateProviderFailure(responseData as unknown));
    }
  } catch (error: any) {
    yield put(actions.activateProviderFailure(error?.response?.data));
  }
}

function isValidProviderResponse(
  response: unknown,
): response is SingleProviderResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'providerId' in response &&
    'partnerId' in response &&
    'name' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidCreateProviderResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) ||
    response.message === 'Successfully created a provider.'
  );
}
function isValidProvidersResponse(response: ApiResponse): boolean {
  return isBoolean(response.success) && response.success === true;
}

function* providerSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetProvidersAction>('provider/getProviders', getProviders);
  yield takeEvery<GetProviderAction>('provider/getProvider', getProvider);
  yield takeEvery<CreateProviderAction>(
    'provider/createProvider',
    createProvider,
  );
  yield takeEvery<UpdateProviderAction>(
    'provider/updateProvider',
    updateProvider,
  );
  yield takeEvery<DeleteProviderAction>(
    'provider/deleteProvider',
    deleteProvider,
  );
  yield takeEvery<ActivateProviderAction>(
    'provider/activateProvider',
    activateProvider,
  );
}

export default providerSaga;
